import { cn } from '../../lib/utils';

const priorities = [
  {
    value: 'low',
    label: 'Low',
    dot: 'bg-emerald-500',
    activeBg: 'bg-emerald-50 dark:bg-emerald-950/40',
    activeText: 'text-emerald-700 dark:text-emerald-300',
    activeBorder: 'border-emerald-200 dark:border-emerald-800',
    activeRing: 'ring-emerald-500/20'
  },
  {
    value: 'medium',
    label: 'Medium',
    dot: 'bg-amber-500',
    activeBg: 'bg-amber-50 dark:bg-amber-950/40',
    activeText: 'text-amber-700 dark:text-amber-300',
    activeBorder: 'border-amber-200 dark:border-amber-800',
    activeRing: 'ring-amber-500/20'
  },
  {
    value: 'high',
    label: 'High',
    dot: 'bg-orange-500',
    activeBg: 'bg-orange-50 dark:bg-orange-950/40',
    activeText: 'text-orange-700 dark:text-orange-300',
    activeBorder: 'border-orange-200 dark:border-orange-800',
    activeRing: 'ring-orange-500/20'
  },
  {
    value: 'urgent',
    label: 'Urgent',
    dot: 'bg-red-500',
    activeBg: 'bg-red-50 dark:bg-red-950/40',
    activeText: 'text-red-700 dark:text-red-300',
    activeBorder: 'border-red-200 dark:border-red-800',
    activeRing: 'ring-red-500/20'
  }
] as const;

export type Priority = (typeof priorities)[number]['value'];

interface PrioritySelectorProps {
  value: Priority;
  onChange: (value: Priority) => void;
  className?: string;
}

export function PrioritySelector({ value, onChange, className }: PrioritySelectorProps) {
  return (
    <div className={cn("inline-flex items-center rounded-lg border bg-muted/50 dark:bg-gray-800/50 p-1 gap-1 w-fit", className)}>
      {priorities.map((priority) => {
        const isSelected = value === priority.value;
        return (
          <button
            key={priority.value}
            type="button"
            onClick={() => onChange(priority.value)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all cursor-pointer',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
              isSelected
                ? cn(
                    'border shadow-sm ring-1',
                    priority.activeBg,
                    priority.activeText,
                    priority.activeBorder,
                    priority.activeRing
                  )
                : 'border border-transparent text-muted-foreground dark:text-gray-400 hover:text-foreground dark:hover:text-gray-100 hover:bg-muted dark:hover:bg-gray-700'
            )}
          >
            <span
              className={cn(
                'size-2 rounded-full shrink-0',
                priority.dot,
                isSelected && 'shadow-sm'
              )}
            />
            {priority.label}
          </button>
        );
      })}
    </div>
  );
}
